/**
 * StudentShellHeader — l'en-tête de l'espace élève, et sa feuille « moi ».
 *
 * C'est de la chrome d'espace, pas un écran : il ne charge rien. Il lit la coquille dans le store
 * (`studentShellViewModel`) et rend le `<ParentHeader>` du DS habillé pour l'élève, comme la
 * maquette (`StudentSpace.stories.helpers.tsx`) : la pastille signée — son avatar, son prénom, ni
 * légende ni chevron —, le point d'alerte, et ses compteurs dans la fente, SAUF sur l'accueil, où la
 * page les dit sous le salut (une seule ligne de compteurs par écran).
 *
 * L'avatar porte le pictogramme de la formule de l'élève, comme l'en-tête d'aujourd'hui.
 *
 * La marque Masteur ramène à l'accueil (l'onglet `home`), d'un toucher, sans changer son dessin.
 *
 * La pastille ouvre la feuille « moi » (`<ProfileSwitcherSheet me>`) : l'élève en tête et « Mon
 * compte › », ses frère et sœur sous « Changer de profil » puis « Espace parent », « Apparence »
 * (Automatique par défaut), « Se déconnecter ».
 */
import { useNavigation } from "@react-navigation/native"
import React from "react"
import { useSelector } from "react-redux"

import parentIcon from "../../../../../../assets/avatar/default/parent-512.png"
import { ParentHeader, ProfileSwitcherSheet } from "../../../../../design-system"
import { StudentCounters } from "../../../../../design-system/student"
import { studentShellViewModel } from "../../../view-models/student/StudentShell/studentShellViewModel"
import { setStudentAppearancePreference, useStudentAppearancePreference } from "../../hooks/studentAppearancePreference"
import { STUDENT_OFFER_IMAGES } from "./studentOfferImages"
import { STUDENT_TAB_KEY_TO_ROUTE } from "./studentRoutes"
import { useStudentShellState } from "./useStudentShellState"

export type StudentShellHeaderProps = {
    /** L'onglet montré est l'accueil : l'en-tête n'y porte pas les compteurs. */
    onHome?: boolean
}

export function StudentShellHeader(props: StudentShellHeaderProps) {
    const { onHome = false } = props
    const vm = useSelector(studentShellViewModel)
    const shell = useStudentShellState(vm)
    const appearance = useStudentAppearancePreference()
    const navigation = useNavigation() as unknown as { navigate: (route: string) => void }

    return (
        <>
            <ParentHeader
                parentFirstname={vm.firstname}
                onSwitchProfile={shell.openSheet}
                profileDisabled={shell.switchingId != null}
                profile={{
                    legend: "",
                    avatarUri: vm.avatarUri,
                    alert: vm.alert,
                    badgeSource: vm.offer != null ? STUDENT_OFFER_IMAGES[vm.offer] : undefined,
                    chevron: false,
                    signed: true,
                    accessibilityLabel: "Mon espace",
                }}
                center={!onHome && vm.counters != null ? <StudentCounters {...vm.counters} /> : undefined}
                onPressLogo={() => navigation.navigate(STUDENT_TAB_KEY_TO_ROUTE.home)}
            />

            <ProfileSwitcherSheet
                visible={shell.sheetOpen}
                onClose={shell.closeSheet}
                me={vm.me}
                profiles={vm.profiles}
                selectedProfileId={vm.userId}
                switchingProfileId={shell.switchingId}
                onSelectProfile={shell.selectProfile}
                onOpenAccountSettings={shell.openAccount}
                parentAvatarSource={parentIcon}
                parentActive={false}
                onSelectParent={shell.enterParentSpace}
                onSwitchAccount={shell.logout}
                switchingAccount={shell.loggingOut}
                appearance={{ value: appearance, onChange: setStudentAppearancePreference }}
            />
        </>
    )
}
